import { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { API } from '../api/client';
import type { ApiError } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { cop, fecha, hoy, toDateValue } from '../lib/format';
import { downloadXlsx, FMT_COP, FMT_DATE, FMT_INT, fileNameConFecha } from '../lib/xlsx';
import type { EstadoInversion, EstadoOperativo, Inversion } from '../api/types';
import { PageHeader } from '../components/PageHeader';
import { ResponsiveTable, type Column } from '../components/ResponsiveTable';
import { MobileFormSheet } from '../components/MobileFormSheet';
import { useIsMobile } from '../lib/useMediaQuery';

type FormInv = {
  id?: string;
  nombre: string;
  fecha: string;
  valor: string;
  mesesVidaUtil: string;
  nota: string;
  estado: EstadoInversion;
  esEquipo: boolean;
  marca: string;
  modelo: string;
  serial: string;
  ubicacion: string;
  estadoOperativo: EstadoOperativo | '';
};

type InversionesResp = {
  items: Inversion[];
  totalInversiones?: number;
  depreciacionMensual?: number;
};

const empty: FormInv = {
  nombre: '',
  fecha: hoy(),
  valor: '',
  mesesVidaUtil: '60',
  nota: '',
  estado: 'activo',
  esEquipo: false,
  marca: '',
  modelo: '',
  serial: '',
  ubicacion: '',
  estadoOperativo: '',
};

export function InversionesPage() {
  const { tiene } = useAuth();
  const qc = useQueryClient();
  const isMobile = useIsMobile();
  const [form, setForm] = useState<FormInv>(empty);
  const [filtro, setFiltro] = useState<'todos' | 'equipos' | 'otros'>('todos');
  const [error, setError] = useState<string | null>(null);
  const [sheetOpen, setSheetOpen] = useState(false);

  const q = useQuery({
    queryKey: ['inversiones'],
    queryFn: () => API.get<InversionesResp>('/inversiones'),
    enabled: tiene('inversiones:read'),
  });

  const lista = useMemo(() => {
    const xs = q.data?.items ?? [];
    if (filtro === 'equipos') return xs.filter((i) => i.esEquipo);
    if (filtro === 'otros') return xs.filter((i) => !i.esEquipo);
    return xs;
  }, [q.data, filtro]);

  const totalValor = lista.reduce((a, i) => a + Number(i.valor), 0);
  const totalDepre = lista.reduce((a, i) => a + (i.depreciacionMensual ?? 0), 0);

  const guardar = useMutation({
    mutationFn: (body: FormInv) => {
      const payload = {
        nombre: body.nombre,
        fecha: body.fecha,
        valor: Number(body.valor.replace(',', '.')),
        mesesVidaUtil: Number(body.mesesVidaUtil),
        nota: body.nota || null,
        estado: body.estado,
        esEquipo: body.esEquipo,
        marca: body.marca || null,
        modelo: body.modelo || null,
        serial: body.serial || null,
        ubicacion: body.ubicacion || null,
        estadoOperativo: body.estadoOperativo || null,
      };
      return body.id
        ? API.put<Inversion>(`/inversiones/${body.id}`, payload)
        : API.post<Inversion>('/inversiones', payload);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['inversiones'] });
      qc.invalidateQueries({ queryKey: ['inversion-acumulada'] });
      setForm(empty);
      setError(null);
      setSheetOpen(false);
    },
    onError: (err) => setError((err as unknown as ApiError).message ?? 'Error'),
  });

  const eliminar = useMutation({
    mutationFn: (id: string) => API.del(`/inversiones/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['inversiones'] });
      qc.invalidateQueries({ queryKey: ['inversion-acumulada'] });
    },
    onError: (err) => setError((err as unknown as ApiError).message ?? 'Error'),
  });

  function exportarXlsx() {
    if (lista.length === 0) return;
    downloadXlsx(fileNameConFecha('inversiones'), [
      {
        name: 'Inversiones',
        rows: lista,
        columns: [
          { header: 'Nombre', value: (i) => i.nombre, width: 22 },
          { header: 'Es equipo', value: (i) => (i.esEquipo ? 'Sí' : 'No'), width: 10 },
          { header: 'Fecha', value: (i) => toDateValue(i.fecha), width: 12, numFmt: FMT_DATE },
          { header: 'Valor', value: (i) => Number(i.valor), width: 14, numFmt: FMT_COP },
          { header: 'Meses vida útil', value: (i) => i.mesesVidaUtil, width: 12, numFmt: FMT_INT },
          { header: 'Depreciación mensual', value: (i) => i.depreciacionMensual ?? 0, width: 16, numFmt: FMT_COP },
          { header: 'Estado', value: (i) => i.estado, width: 10 },
          { header: 'Marca', value: (i) => i.marca ?? '', width: 14 },
          { header: 'Modelo', value: (i) => i.modelo ?? '', width: 14 },
          { header: 'Serial', value: (i) => i.serial ?? '', width: 14 },
          { header: 'Ubicación', value: (i) => i.ubicacion ?? '', width: 14 },
          { header: 'Estado operativo', value: (i) => (i.estadoOperativo ?? '').replace('_', ' '), width: 16 },
          { header: 'Nota', value: (i) => i.nota ?? '', width: 30 },
        ],
      },
    ]);
  }

  function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!form.nombre.trim() || !form.valor || !form.mesesVidaUtil) {
      setError('Nombre, valor y meses de vida útil son obligatorios');
      return;
    }
    guardar.mutate(form);
  }

  function openEdit(i: Inversion) {
    setForm({
      id: i.id,
      nombre: i.nombre,
      fecha: i.fecha.slice(0, 10),
      valor: i.valor,
      mesesVidaUtil: String(i.mesesVidaUtil),
      nota: i.nota ?? '',
      estado: i.estado,
      esEquipo: i.esEquipo,
      marca: i.marca ?? '',
      modelo: i.modelo ?? '',
      serial: i.serial ?? '',
      ubicacion: i.ubicacion ?? '',
      estadoOperativo: i.estadoOperativo ?? '',
    });
    if (isMobile) setSheetOpen(true);
  }

  const columns: Column<Inversion>[] = [
    {
      key: 'nombre',
      header: 'Nombre',
      mobile: 'primary',
      render: (i) => (
        <div>
          <div className="font-medium truncate">{i.nombre}</div>
          {i.esEquipo && (
            <div className="text-xs text-slate-500 truncate">
              {[i.marca, i.modelo].filter(Boolean).join(' ')}
              {i.ubicacion && ` · ${i.ubicacion}`}
            </div>
          )}
        </div>
      ),
    },
    {
      key: 'valor',
      header: 'Valor',
      mobile: 'secondary',
      align: 'right',
      render: (i) => cop(Number(i.valor)),
    },
    { key: 'fecha', header: 'Fecha', render: (i) => fecha(i.fecha) },
    { key: 'vida', header: 'Vida útil', render: (i) => `${i.mesesVidaUtil} m` },
    { key: 'depre', header: 'Depre./mes', align: 'right', render: (i) => cop(i.depreciacionMensual) },
    {
      key: 'estado',
      header: 'Estado',
      render: (i) => (
        <>
          <span className={`pill ${i.estado === 'activo' ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>
            {i.estado}
          </span>
          {i.esEquipo && i.estadoOperativo && (
            <div className="text-xs text-slate-500 mt-0.5">{i.estadoOperativo.replace('_', ' ')}</div>
          )}
        </>
      ),
    },
  ];

  const formJsx = (
    <form className="space-y-3" onSubmit={submit}>
      <div>
        <label className="label">Nombre</label>
        <input className="input" value={form.nombre} onChange={(e) => setForm((s) => ({ ...s, nombre: e.target.value }))} required />
      </div>
      <div className="grid grid-cols-1 xs:grid-cols-2 gap-3">
        <div>
          <label className="label">Fecha</label>
          <input type="date" className="input" value={form.fecha} onChange={(e) => setForm((s) => ({ ...s, fecha: e.target.value }))} required />
        </div>
        <div>
          <label className="label">Valor (COP)</label>
          <input type="number" step="any" inputMode="decimal" className="input" value={form.valor} onChange={(e) => setForm((s) => ({ ...s, valor: e.target.value }))} required />
        </div>
        <div>
          <label className="label">Meses vida útil</label>
          <input type="number" min="1" inputMode="numeric" className="input" value={form.mesesVidaUtil} onChange={(e) => setForm((s) => ({ ...s, mesesVidaUtil: e.target.value }))} required />
        </div>
        <div>
          <label className="label">Estado</label>
          <select className="input" value={form.estado} onChange={(e) => setForm((s) => ({ ...s, estado: e.target.value as EstadoInversion }))}>
            <option value="activo">Activo</option>
            <option value="retirado">Retirado</option>
          </select>
        </div>
      </div>

      <label className="flex items-center gap-2 text-sm">
        <input type="checkbox" checked={form.esEquipo} onChange={(e) => setForm((s) => ({ ...s, esEquipo: e.target.checked }))} />
        Es un equipo físico (licuadora, molino, etc.)
      </label>

      {form.esEquipo && (
        <>
          <div className="grid grid-cols-1 xs:grid-cols-2 gap-3">
            <div>
              <label className="label">Marca</label>
              <input className="input" value={form.marca} onChange={(e) => setForm((s) => ({ ...s, marca: e.target.value }))} />
            </div>
            <div>
              <label className="label">Modelo</label>
              <input className="input" value={form.modelo} onChange={(e) => setForm((s) => ({ ...s, modelo: e.target.value }))} />
            </div>
            <div>
              <label className="label">Serial</label>
              <input className="input" value={form.serial} onChange={(e) => setForm((s) => ({ ...s, serial: e.target.value }))} />
            </div>
            <div>
              <label className="label">Ubicación</label>
              <input className="input" value={form.ubicacion} onChange={(e) => setForm((s) => ({ ...s, ubicacion: e.target.value }))} />
            </div>
          </div>
          <div>
            <label className="label">Estado operativo</label>
            <select className="input" value={form.estadoOperativo} onChange={(e) => setForm((s) => ({ ...s, estadoOperativo: e.target.value as EstadoOperativo | '' }))}>
              <option value="">—</option>
              <option value="funcionando">Funcionando</option>
              <option value="en_reparacion">En reparación</option>
              <option value="fuera_de_servicio">Fuera de servicio</option>
            </select>
          </div>
        </>
      )}

      <div>
        <label className="label">Nota</label>
        <textarea className="input min-h-[50px]" value={form.nota} onChange={(e) => setForm((s) => ({ ...s, nota: e.target.value }))} />
      </div>

      {error && <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded p-2">{error}</div>}

      <div className="flex gap-2">
        <button type="submit" className="btn-primary flex-1" disabled={guardar.isPending}>
          {form.id ? 'Guardar' : 'Crear'}
        </button>
        {form.id && <button type="button" className="btn-secondary" onClick={() => { setForm(empty); setError(null); }}>Cancelar</button>}
      </div>
    </form>
  );

  return (
    <div className="space-y-4 sm:space-y-6">
      <PageHeader
        title="Inversiones y equipos"
        subtitle="Activos del negocio. La depreciación mensual alimenta el costo fijo por paquete."
      />

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 sm:gap-3 items-stretch">
        <div className="card px-3 py-2">
          <div className="text-[10px] uppercase text-slate-500">Valor total</div>
          <div className="text-base sm:text-lg font-semibold">{cop(totalValor)}</div>
        </div>
        <div className="card px-3 py-2">
          <div className="text-[10px] uppercase text-slate-500">Depre./mes</div>
          <div className="text-base sm:text-lg font-semibold">{cop(totalDepre)}</div>
        </div>
        <button
          type="button"
          className="btn-secondary col-span-2 sm:col-span-1"
          onClick={exportarXlsx}
          disabled={lista.length === 0}
          title="Descargar como .xlsx"
        >
          ⬇ Exportar .xlsx
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 lg:gap-6">
        {tiene('inversiones:write') && !isMobile && (
          <div className="card p-5 space-y-3 lg:col-span-1">
            <h2 className="font-semibold">{form.id ? 'Editar' : 'Nueva inversión / equipo'}</h2>
            {formJsx}
          </div>
        )}

        <div className={`card ${tiene('inversiones:write') && !isMobile ? 'lg:col-span-2' : 'lg:col-span-3'}`}>
          <div className="p-3 border-b border-slate-200 flex gap-2 text-sm overflow-x-auto">
            {(['todos', 'equipos', 'otros'] as const).map((f) => (
              <button
                key={f}
                type="button"
                className={`chip whitespace-nowrap ${filtro === f ? 'chip-active' : ''}`}
                onClick={() => setFiltro(f)}
              >
                {f === 'todos' ? 'Todos' : f === 'equipos' ? 'Equipos' : 'Otras inversiones'}
              </button>
            ))}
          </div>
          <ResponsiveTable<Inversion>
            columns={columns}
            rows={lista}
            rowKey={(i) => i.id}
            loading={q.isLoading}
            empty="Sin registros."
            actions={(i) =>
              tiene('inversiones:write') ? (
                <>
                  <button type="button" className="btn-secondary text-xs" onClick={() => openEdit(i)}>
                    Editar
                  </button>
                  <button
                    type="button"
                    className="btn-danger text-xs"
                    onClick={() => { if (confirm(`¿Eliminar "${i.nombre}"?`)) eliminar.mutate(i.id); }}
                  >
                    Eliminar
                  </button>
                </>
              ) : null
            }
          />
        </div>
      </div>

      {tiene('inversiones:write') && (
        <>
          <button
            type="button"
            onClick={() => { setForm(empty); setError(null); setSheetOpen(true); }}
            className="md:hidden fab"
            aria-label="Nueva inversión"
          >
            +
          </button>
          <MobileFormSheet
            open={sheetOpen}
            onClose={() => setSheetOpen(false)}
            title={form.id ? 'Editar' : 'Nueva inversión / equipo'}
          >
            {formJsx}
          </MobileFormSheet>
        </>
      )}
    </div>
  );
}
